import React, { useEffect, useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
    Box, Grid, TextField, Typography, Button, Card, CardContent,
    FormControlLabel, Checkbox, ToggleButtonGroup, ToggleButton,
    InputAdornment, Divider,
} from "@mui/material";
import { styles } from "../../utils/styles";
import { sendDataWithGet } from "../../services/services";

const DRAFT_KEY = "onboarding_draft";
const loadStepData = (stepIndex) => {
    try {
        const raw = window.localStorage.getItem(DRAFT_KEY);
        if (!raw) return null;
        const data = JSON.parse(raw);
        return data.allFormData ? data.allFormData[stepIndex] : null;
    } catch {
        return null;
    }
};

// --- Constants ---

const CARD_PRODUCTS = [
    { key: "visa_mc", label: "VISA / MASTERCARD" },
    { key: "diners", label: "DINERS" },
    { key: "amex", label: "AMEX" },
    { key: "upi", label: "UNIONPAY INTERNATIONAL (UPI)" },
    { key: "discover", label: "DISCOVER" },
    { key: "jcb", label: "JCB" },
];

// Layout matches PDF pages 3 & 4: 3 columns × 3 rows
const APM_EWALLET_ROWS = [
    [
        { key: "paynow", label: "PayNow" },
        { key: "grabpay", label: "GrabPay" },
        { key: "upi_india", label: "UPI India" },
    ],
    [
        { key: "alipay", label: "AllPay+" },
        { key: "spay", label: "SPay" },
        { key: "roam_qr", label: "RoamQR" },
    ],
    [
        { key: "wechat", label: "WeChat Pay" },
        { key: "dash", label: "DASH" },
        null,
    ],
];

// Virtual/online terminal: no RoamQR support (v3.7.0 e-commerce table has UPI India instead)
const APM_EWALLET_ROWS_ONLINE = APM_EWALLET_ROWS.map((row) =>
    row.map((item) => (item?.key === "roam_qr" ? null : item))
);

const BNPL_PRODUCTS = [
    { key: "atome", label: "ATOME" },
    { key: "grab_later", label: "Grab PayLater" },
    { key: "spay_basic", label: "Spay + SPayLater BASIC" },
    { key: "spay_premium", label: "Spay + SPayLater PREMIUM" },
];

const IPP_MONTHS = [3, 6, 12, 24];


// --- Data factories ---

const emptyCard = () => ({
    enabled: false,
    local_rate: "",
    foreign_rate: "",
    transaction_fee_local: "",
    transaction_fee_foreign: "",
});
const emptyApm = () => ({ enabled: false, rate: "" });
const emptyBnpl = () => ({ enabled: false, rate: "", transaction_fee: "" });
const emptyIpp = () => ({ enabled: false, rate: "" });

export const emptyRatesSection = () => ({
    settlement_cycle: "",
    cards: {
        visa_mc: emptyCard(),
        diners: emptyCard(),
        amex: emptyCard(),
        upi: emptyCard(),
        discover: emptyCard(),
        jcb: emptyCard(),
    },
    apm: {
        paynow: emptyApm(),
        grabpay: emptyApm(),
        upi_india: emptyApm(),
        alipay: emptyApm(),
        spay: emptyApm(),
        roam_qr: emptyApm(),
        wechat: emptyApm(),
        dash: emptyApm(),
    },
    bnpl: {
        atome: emptyBnpl(),
        grab_later: emptyBnpl(),
        spay_basic: emptyBnpl(),
        spay_premium: emptyBnpl(),
    },
    ipp: {
        months_3: emptyIpp(),
        months_6: emptyIpp(),
        months_12: emptyIpp(),
        months_24: emptyIpp(),
    },
    min_transaction_amount: "",
});

export const emptyMerchantRates = (merchantId, merchantName) => ({
    merchant_id: merchantId || "",
    merchant_name: merchantName || "",
    terminal_mode: "",
    physical: emptyRatesSection(),
    online: emptyRatesSection(),
});

// --- ProductRatesSection ---

const SX_SECTION_BORDER = { border: "1px solid", borderColor: "divider", mb: 2 };
const SX_SECTION_HEADER = {
    bgcolor: "grey.100",
    px: 1.5,
    py: 0.75,
    borderBottom: "1px solid",
    borderColor: "divider",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
};

export const ProductRatesSection = ({ prefix, values, setFieldValue, handleChange, handleBlur, readOnly, paymentCycleOptions = [], apmRows = APM_EWALLET_ROWS }) => {
    const s = values;

    const rateField = (name, disabled, extraProps = {}) => (
        <TextField
            fullWidth
            size="small"
            name={name}
            value={name.split(".").reduce((o, k) => (o ?? {})[k], { [prefix]: s }) ?? ""}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={readOnly || disabled}
            type="number"
            inputProps={{ min: 0, step: "any" }}
            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
            {...extraProps}
        />
    );

    const getVal = (path) => path.split(".").reduce((o, k) => (o ?? {})[k], s);

    return (
        <Box>
            {/* ── Settlement T+ ── */}
            <Box display="flex" justifyContent="flex-end" alignItems="center" gap={1} mb={1}>
                <Typography variant="caption" fontWeight="bold">SETTLEMENT T +</Typography>
                <TextField
                    select
                    size="small"
                    sx={{ width: 150 }}
                    name={`${prefix}.settlement_cycle`}
                    value={s.settlement_cycle || ""}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={readOnly}
                    SelectProps={{ native: true }}
                >
                    <option value="">Select</option>
                    {paymentCycleOptions.map(({ id, name }) => (
                        <option key={id} value={id}>{name}</option>
                    ))}
                </TextField>
            </Box>

            {/* ── PRODUCT - CARDS ── */}
            <Box sx={SX_SECTION_BORDER}>
                <Box sx={SX_SECTION_HEADER}>
                    <Typography variant="subtitle2" fontWeight="bold">PRODUCT - CARDS</Typography>
                    <Box display="flex" gap={1}>
                        <Typography variant="caption" sx={{ width: 90, textAlign: "center" }}>Local Cards (%)</Typography>
                        <Typography variant="caption" sx={{ width: 110, textAlign: "center" }}>Local Per Transaction</Typography>
                        <Typography variant="caption" sx={{ width: 90, textAlign: "center" }}>Foreign Cards (%)</Typography>
                        <Typography variant="caption" sx={{ width: 110, textAlign: "center" }}>Foreign Per Transaction</Typography>
                    </Box>
                </Box>

                {CARD_PRODUCTS.map(({ key, label }) => {
                    const enabled = Boolean(s.cards[key].enabled);
                    return (
                        <Box
                            key={key}
                            display="flex"
                            alignItems="center"
                            gap={1}
                            px={1.5}
                            py={0.5}
                            sx={{ borderTop: "1px solid", borderColor: "divider" }}
                        >
                            <FormControlLabel
                                sx={{ flex: 1, m: 0 }}
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={enabled}
                                        onChange={(e) =>
                                            setFieldValue(`${prefix}.cards.${key}.enabled`, e.target.checked)
                                        }
                                        disabled={readOnly}
                                    />
                                }
                                label={<Typography variant="body2">{label}</Typography>}
                            />
                            <Box sx={{ width: 90 }}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    name={`${prefix}.cards.${key}.local_rate`}
                                    value={s.cards[key].local_rate}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    disabled={readOnly || !enabled}
                                    type="number"
                                    inputProps={{ min: 0, step: "any" }}
                                    InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                                />
                            </Box>
                            <Box sx={{ width: 110 }}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    name={`${prefix}.cards.${key}.transaction_fee_local`}
                                    value={s.cards[key].transaction_fee_local}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    disabled={readOnly || !enabled}
                                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                                />
                            </Box>
                            <Box sx={{ width: 90 }}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    name={`${prefix}.cards.${key}.foreign_rate`}
                                    value={s.cards[key].foreign_rate}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    disabled={readOnly || !enabled}
                                    type="number"
                                    inputProps={{ min: 0, step: "any" }}
                                    InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                                />
                            </Box>
                            <Box sx={{ width: 110 }}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    name={`${prefix}.cards.${key}.transaction_fee_foreign`}
                                    value={s.cards[key].transaction_fee_foreign}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    disabled={readOnly || !enabled}
                                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                                />
                            </Box>
                        </Box>
                    );
                })}
            </Box>

            {/* ── ALTERNATIVE PAYMENT METHODS ── */}
            <Box sx={SX_SECTION_BORDER}>
                <Box sx={SX_SECTION_HEADER}>
                    <Typography variant="subtitle2" fontWeight="bold">ALTERNATIVE PAYMENT METHODS</Typography>
                </Box>
                <Box px={1.5} py={1}>

                    {/* E-Wallets/QR */}
                    <Typography variant="caption" fontWeight="bold" display="block" mb={0.5}>
                        E-Wallets/QR
                    </Typography>
                    {apmRows.map((row, rowIdx) => (
                        <Grid container spacing={1} mb={0.5} key={rowIdx}>
                            {row.map((item, colIdx) =>
                                !item ? (
                                    <Grid item xs={4} key={colIdx} />
                                ) : (
                                    <Grid item xs={4} key={item.key}>
                                        <Box display="flex" alignItems="center" gap={0.5}>
                                            <Checkbox
                                                size="small"
                                                sx={{ p: 0.5 }}
                                                checked={Boolean(s.apm[item.key].enabled)}
                                                onChange={(e) =>
                                                    setFieldValue(`${prefix}.apm.${item.key}.enabled`, e.target.checked)
                                                }
                                                disabled={readOnly}
                                            />
                                            <Typography variant="body2" sx={{ minWidth: 80 }}>
                                                {item.label}
                                            </Typography>
                                            <TextField
                                                size="small"
                                                sx={{ width: 80 }}
                                                name={`${prefix}.apm.${item.key}.rate`}
                                                value={s.apm[item.key].rate}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                disabled={readOnly || !s.apm[item.key].enabled}
                                                type="number"
                                                inputProps={{ min: 0, step: "any" }}
                                                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                                            />
                                        </Box>
                                    </Grid>
                                )
                            )}
                        </Grid>
                    ))}

                    <Divider sx={{ my: 1 }} />

                    {/* BNPL */}
                    <Typography variant="caption" fontWeight="bold" display="block" mb={0.5}>
                        Buy Now Pay Later (BNPL)**
                    </Typography>
                    {BNPL_PRODUCTS.map(({ key, label }) => {
                        const enabled = Boolean(s.bnpl[key].enabled);
                        return (
                            <Grid container alignItems="center" spacing={1} mb={0.5} key={key}>
                                <Grid item xs={12} sm={4}>
                                    <FormControlLabel
                                        sx={{ m: 0 }}
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={enabled}
                                                onChange={(e) =>
                                                    setFieldValue(`${prefix}.bnpl.${key}.enabled`, e.target.checked)
                                                }
                                                disabled={readOnly}
                                            />
                                        }
                                        label={<Typography variant="body2">{label}</Typography>}
                                    />
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        name={`${prefix}.bnpl.${key}.rate`}
                                        value={s.bnpl[key].rate}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        disabled={readOnly || !enabled}
                                        type="number"
                                        inputProps={{ min: 0, step: "any" }}
                                        InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                                    />
                                </Grid>
                                <Grid item xs={6} sm={4}>
                                    <Box display="flex" alignItems="center" gap={0.5}>
                                        <Typography variant="caption" noWrap>Transaction Fee</Typography>
                                        <TextField
                                            size="small"
                                            sx={{ width: 100 }}
                                            name={`${prefix}.bnpl.${key}.transaction_fee`}
                                            value={s.bnpl[key].transaction_fee}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            disabled={readOnly || !enabled}
                                            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                                        />
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={1}>
                                    <Typography variant="caption">Per Transaction</Typography>
                                </Grid>
                            </Grid>
                        );
                    })}
                    <Typography variant="caption" color="error" display="block" mb={1}>
                        ** The final MDR and tenure might be subjected to changes and final approval by our providers.
                    </Typography>

                    <Divider sx={{ my: 1 }} />

                    {/* IPP */}
                    <Typography variant="caption" fontWeight="bold" display="block" mb={0.5}>
                        Instalment Payment Plans (IPP)
                    </Typography>
                    <Grid container spacing={1} mb={1}>
                        {IPP_MONTHS.map((months) => {
                            const key = `months_${months}`;
                            const enabled = Boolean(s.ipp[key].enabled);
                            return (
                                <Grid item xs={6} sm={3} key={key}>
                                    <Box display="flex" alignItems="center" gap={0.5}>
                                        <Checkbox
                                            size="small"
                                            sx={{ p: 0.5 }}
                                            checked={enabled}
                                            onChange={(e) =>
                                                setFieldValue(`${prefix}.ipp.${key}.enabled`, e.target.checked)
                                            }
                                            disabled={readOnly}
                                        />
                                        <Typography variant="body2" sx={{ minWidth: 70 }}>{months} months</Typography>
                                        <TextField
                                            size="small"
                                            sx={{ width: 72 }}
                                            name={`${prefix}.ipp.${key}.rate`}
                                            value={s.ipp[key].rate}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            disabled={readOnly || !enabled}
                                            type="number"
                                            inputProps={{ min: 0, step: "any" }}
                                            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                                        />
                                    </Box>
                                </Grid>
                            );
                        })}
                    </Grid>

                    {/* Min Transaction Amount */}
                    <Box display="flex" alignItems="center" gap={1} mt={1}>
                        <Typography variant="body2">Minimum Transaction Amount:</Typography>
                        <TextField
                            size="small"
                            sx={{ width: 130 }}
                            name={`${prefix}.min_transaction_amount`}
                            value={s.min_transaction_amount}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            disabled={readOnly}
                            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                        />
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

// --- Main component ---

const validationSchema = Yup.object().shape({
    merchants: Yup.array().of(
        Yup.object().shape({
            terminal_mode: Yup.string().required("Please select a terminal type"),
        })
    ),
});

const Step3Terminal = ({
    formData,
    merchantData,
    onNext,
    onBack,
    isSubmitting,
    readOnly = false,
    onSkip,
}) => {
    const [paymentCycleOptions, setPaymentCycleOptions] = useState([]);

    useEffect(() => {
        sendDataWithGet("/bank_account/merchant_payout_info/static", (data) => {
            setPaymentCycleOptions(data?.payment_cycle || []);
        });
    }, []);

    const buildInitialValues = () => {
        const savedStep3 = loadStepData(2);
        if (savedStep3?.merchants?.length) return savedStep3;
        if (formData?.merchants?.length) return formData;

        const merchants = merchantData?.items || [];
        return {
            merchants: merchants.map((merchant) =>
                emptyMerchantRates(merchant.id || "", merchant.name || "")
            ),
        };
    };

    return (
        <Formik
            initialValues={buildInitialValues()}
            validationSchema={validationSchema}
            onSubmit={(values, actions) => onNext(values, actions)}
            enableReinitialize
        >
            {({ values, handleChange, handleBlur, setFieldValue, errors, touched }) => (
                <Form autoComplete="off" noValidate>
                    <Grid container spacing={3} sx={{ py: 2, px: 2 }}>
                        {values.merchants.map((merchant, idx) => (
                            <Grid item xs={12} key={idx}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>
                                            {merchant.merchant_name || `Merchant ${idx + 1}`}
                                        </Typography>

                                        {/* Terminal mode selector */}
                                        <Box mb={2}>
                                            <Typography variant="body2" mb={0.5}>Terminal Type *</Typography>
                                            <ToggleButtonGroup
                                                value={merchant.terminal_mode}
                                                exclusive
                                                onChange={(_, val) => {
                                                    if (val !== null)
                                                        setFieldValue(`merchants.${idx}.terminal_mode`, val);
                                                }}
                                                disabled={isSubmitting || readOnly}
                                                size="small"
                                            >
                                                <ToggleButton value="physical">Physical</ToggleButton>
                                                <ToggleButton value="online">Online</ToggleButton>
                                                <ToggleButton value="both">Both</ToggleButton>
                                            </ToggleButtonGroup>
                                            {touched.merchants?.[idx]?.terminal_mode &&
                                                errors.merchants?.[idx]?.terminal_mode && (
                                                    <Typography variant="caption" color="error" display="block" mt={0.5}>
                                                        {errors.merchants[idx].terminal_mode}
                                                    </Typography>
                                                )}
                                        </Box>

                                        {/* Physical section */}
                                        {(merchant.terminal_mode === "physical" ||
                                            merchant.terminal_mode === "both") && (
                                            <Box mb={3}>
                                                {merchant.terminal_mode === "both" && (
                                                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                                        Physical Terminal
                                                    </Typography>
                                                )}
                                                <ProductRatesSection
                                                    prefix={`merchants.${idx}.physical`}
                                                    values={merchant.physical}
                                                    setFieldValue={setFieldValue}
                                                    handleChange={handleChange}
                                                    handleBlur={handleBlur}
                                                    readOnly={isSubmitting || readOnly}
                                                    paymentCycleOptions={paymentCycleOptions}
                                                />
                                            </Box>
                                        )}

                                        {/* Online section */}
                                        {(merchant.terminal_mode === "online" ||
                                            merchant.terminal_mode === "both") && (
                                            <Box mb={3}>
                                                {merchant.terminal_mode === "both" && (
                                                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                                        Online / E-Commerce
                                                    </Typography>
                                                )}
                                                <ProductRatesSection
                                                    prefix={`merchants.${idx}.online`}
                                                    values={merchant.online}
                                                    setFieldValue={setFieldValue}
                                                    handleChange={handleChange}
                                                    handleBlur={handleBlur}
                                                    readOnly={isSubmitting || readOnly}
                                                    paymentCycleOptions={paymentCycleOptions}
                                                    apmRows={APM_EWALLET_ROWS_ONLINE}
                                                />
                                            </Box>
                                        )}

                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>

                    <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4, px: 2, pb: 2 }}>
                        <Button onClick={onBack} variant="outlined" disabled={isSubmitting || readOnly}>
                            Back
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={isSubmitting || readOnly}
                            sx={{ ...styles.customButton }}
                        >
                            {isSubmitting ? "Saving..." : "Save & Continue"}
                        </Button>
                    </Box>
                </Form>
            )}
        </Formik>
    );
};

export default Step3Terminal;
